/**
 * What a piece of wastage costs. See docs/architecture.md §5 and the
 * `// ---- wastage` block in design/project/Bar OS.dc.html.
 *
 * The prototype computes this in the component; here it is pure and shared,
 * because the API stores the figure and the tablet shows it before the write
 * lands. If those two disagreed by a cent the week's total would not add up.
 *
 * Nothing in this module reads the table. Callers resolve the ingredient or
 * the spec first and hand the numbers in.
 */
import { toCents } from "./calc.js";
// A bottle with no size on file is a 700, and screen 05 already says so.
// Two copies of that number would be two things to change when a venue
// standardises on litres, so this is the stocktake's constant, not a second
// one that happens to agree today.
import { DEFAULT_BOTTLE_ML } from "./stock.js";
export { DEFAULT_BOTTLE_ML };
export const WASTAGE_UNITS = ["ml", "serve", "bottle"];
/** The units each kind of wastage accepts. A serve of a bottle is not a thing. */
export const UNITS_FOR_KIND = {
    ingredient: ["ml", "bottle"],
    spec: ["serve"],
};
export function unitAllowed(kind, unit) {
    return UNITS_FOR_KIND[kind].includes(unit);
}
/** Round to whole cents and back to dollars — money is never left as float dust. */
function cents(dollars) {
    return toCents(dollars) / 100;
}
/**
 * Millilitres and cost for a wasted ingredient.
 *
 * A bottle is its own size where one is known — 1 L of Campari against 700 mL
 * of Montenegro is a $20 difference on one breakage, so the default is a
 * fallback, not an assumption.
 */
export function ingredientWastage(qty, unit, ingredient) {
    // A declared size of 0 is a missing size, not a bottle that holds nothing —
    // the same reading `bottleSpec` takes in stock.ts.
    const declared = ingredient.bottleMl;
    const bottleMl = typeof declared === "number" && declared > 0 ? declared : DEFAULT_BOTTLE_ML;
    const ml = unit === "bottle" ? qty * bottleMl : qty;
    return { ml, costDollars: cents(ml * ingredient.costPerMl) };
}
/**
 * Millilitres and cost for whole serves of a spec.
 *
 * The cost is the spec's cost per serve, sundries included — a comped drink
 * costs the venue its garnish and its straw as well as its liquid. The volume
 * is the build's, so a comp shows against pour volume like anything else.
 */
export function specWastage(serves, totals) {
    return { ml: serves * totals.volumeMl, costDollars: cents(serves * totals.cost) };
}
/**
 * The one entry point the API and the SPA both call.
 *
 * A quantity that is not a finite, non-negative number is nothing wasted
 * rather than a throw: the tablet asks for this on every keystroke, and a
 * half-typed "1." must not blow up the screen.
 */
export function wastageCost(subject, qty, unit) {
    if (!Number.isFinite(qty) || qty <= 0)
        return { ml: 0, costDollars: 0 };
    return subject.kind === "ingredient"
        ? ingredientWastage(qty, unit, subject.ingredient)
        : specWastage(qty, subject.totals);
}
/**
 * Totals for a period. Summed in cents, so a hundred entries of $0.005 do not
 * drift the way repeated float addition would.
 */
export function wastageSummary(rows) {
    let total = 0;
    const byReason = new Map();
    for (const row of rows) {
        const c = toCents(row.costDollars);
        total += c;
        byReason.set(row.reason, (byReason.get(row.reason) ?? 0) + c);
    }
    return {
        count: rows.length,
        costDollars: total / 100,
        byReason: [...byReason.entries()]
            .map(([reason, c]) => ({ reason, costDollars: c / 100 }))
            .sort((a, b) => b.costDollars - a.costDollars || a.reason.localeCompare(b.reason)),
    };
}
//# sourceMappingURL=wastage.js.map